/**
 * =============================================================================
 * Hook: useAffiliateMyMemberships
 * Versão: 1.0.0 (Fase 3 — Painel multi-programa)
 *
 * Descrição:
 *   Retorna todas as adesões (`affiliate_program_memberships`) do afiliado
 *   ativo no painel `/afiliado/*`. Faz join com `affiliate_programs` para
 *   trazer nome/slug do programa e o `min_payout_amount` (mínimo de saque
 *   por programa, usado nas telas de saldo/saques).
 *
 *   Decisão de UX: o painel NÃO usa seletor de programa. As páginas exibem
 *   tudo separado por programa de forma transparente.
 * =============================================================================
 */
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAffiliateAuth, type AffiliateStatus } from '@/hooks/useAffiliateAuth';
import { createLogger } from '@/lib/logger';

const log = createLogger('AffiliateMyMemberships');

/** Adesão do afiliado em um programa específico, com dados do programa. */
export interface MyAffiliateMembership {
  id: string;
  affiliate_id: string;
  program_id: string;
  status: AffiliateStatus;
  available_balance: number;
  total_earned: number;
  total_paid: number;
  total_clicks: number;
  total_referrals: number;
  total_conversions: number;
  total_points: number;
  approved_at: string | null;
  created_at: string;

  /** Dados do programa (join). */
  program_name: string;
  program_slug: string;
  min_payout_amount: number;
  default_currency: string;
}

export function useAffiliateMyMemberships() {
  const { affiliate } = useAffiliateAuth();

  return useQuery({
    queryKey: ['affiliate-my-memberships', affiliate?.id],
    enabled: !!affiliate?.id,
    staleTime: 30 * 1000,
    queryFn: async (): Promise<MyAffiliateMembership[]> => {
      log.debug('Carregando memberships via RPC server-side', { affiliateId: affiliate?.id });

      // Acesso direto à tabela foi bloqueado pelo hardening RLS.
      // Toda leitura passa por RPC SECURITY DEFINER com validação de ownership.
      const { data, error } = await supabase.rpc('list_my_affiliate_memberships', {
        p_affiliate_id: affiliate?.id ?? null,
      });

      if (error) {
        log.error('Erro ao carregar memberships (RPC)', error);
        throw error;
      }

      const rows = (data as unknown as any[]) ?? [];

      return rows.map((row) => ({
        id: row.id,
        affiliate_id: row.affiliate_id,
        program_id: row.program_id,
        status: row.status,
        available_balance: Number(row.available_balance ?? 0),
        total_earned: Number(row.total_earned ?? 0),
        total_paid: Number(row.total_paid ?? 0),
        total_clicks: Number(row.total_clicks ?? 0),
        total_referrals: Number(row.total_referrals ?? 0),
        total_conversions: Number(row.total_conversions ?? 0),
        total_points: Number(row.total_points ?? 0),
        approved_at: row.approved_at,
        created_at: row.created_at,
        program_name: row.program_name ?? 'Programa',
        program_slug: row.program_slug ?? '',
        min_payout_amount: Number(row.min_payout_amount ?? 0),
        default_currency: row.default_currency ?? 'BRL',
      }));
    },
  });
}
